import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { useAuth } from '@/auth/AuthProvider';
import { Eye, EyeOff } from 'lucide-react';
import { ReloadIcon } from "@radix-ui/react-icons";
import { AuthApi } from '@/services/api';

export const LoginForm: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [statusMessage, setStatusMessage] = useState<{ type: 'error' | 'success'; message: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    if (params.get('registered') === 'true') {
      setStatusMessage({ type: 'success', message: 'Registration successful! Please check your email for verification before logging in.' });
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!statusMessage && auth.isLoading === false) {
      if (auth.user) {
        const returnTo = new URLSearchParams(location.search).get('returnTo') || '/';
        navigate(decodeURIComponent(returnTo), { replace: true });
      }
    }
  }, [auth.user, auth.isLoading, location, navigate, statusMessage]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatusMessage(null);
    setIsLoading(true);
    
    try {
      const searchParams = new URLSearchParams(location.search);
      const returnTo = searchParams.get('returnTo') || '/';
      
      // Use AuthApi.login directly
      const loginResponse = await AuthApi.login(formData.username, formData.password);
      
      // AuthApi.login throws on error, so if we get here, it succeeded
      // It returns { access_token, user }
      if (loginResponse && loginResponse.access_token && loginResponse.user) {
        auth.handleLoginSuccess(loginResponse);
        navigate(returnTo);
      } else {
         // This case should not happen if AuthApi.login is implemented correctly
         console.error("Login succeeded but response was unexpected:", loginResponse);
         throw new Error("Login failed: Invalid response from server.");
      }

    } catch (error) {
      setStatusMessage({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'An unexpected error occurred' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-100 to-white dark:from-indigo-950 dark:to-gray-900 p-4">
      <Card className="w-full max-w-md shadow-lg border-2 border-indigo-100 dark:border-indigo-900">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Log in to Fore-Poster</CardTitle>
          <CardDescription className="text-center">Enter your credentials to access your account</CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Beta/Development Disclaimer */}
          <Alert className="mb-4 bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300">
            <AlertDescription>
              <p className="font-medium mb-1">🚧 Work in Progress</p>
              <p className="text-xs">
                This application is an AI-assisted prototype currently under development. 
                By registering and using this service, you acknowledge that data may be reset 
                during development and the platform is provided "as is" without warranty.
              </p>
            </AlertDescription>
          </Alert>
          
          {statusMessage && (
            <Alert variant={statusMessage.type === 'error' ? 'destructive' : 'default'} className="mb-4">
              <AlertDescription>{statusMessage.message}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username or Email</Label>
              <Input
                id="username"
                name="username"
                placeholder="Enter your username or email"
                value={formData.username}
                onChange={handleChange}
                disabled={isLoading}
                required
                tabIndex={1}
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label htmlFor="password">Password</Label>
                <Link 
                  to="/reset-password"
                  className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                  tabIndex={4}
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="current-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                  className="pr-10"
                  tabIndex={2}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                  onClick={togglePasswordVisibility}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
              </div>
            </div>

            <Button 
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-800"
              disabled={isLoading}
              tabIndex={3}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </span>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <div className="flex w-full items-center justify-between">
            <div className="text-center text-sm">
              Don't have an account?{' '}
              <Button
                onClick={() => navigate('/register')}
                variant="link"
                className="p-0 h-auto font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                tabIndex={5}
              >
                Register
              </Button>
            </div>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}; 